import { Link, useParams } from "react-router-dom";
import Styles from './users.module.scss';
import {
    FiAlertTriangle,
    FiArrowLeft,
    FiBookOpen,
    FiCalendar,
    FiCreditCard,
    FiDollarSign,
    FiMessageCircle,
    FiPhone,
    FiStar,
    FiUser,
    FiUsers
} from "react-icons/fi";
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import { AppointmentWidget, BarChart, NumberIcon } from "../../components/widgets";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getUserProfiles, getUserWithProfiles, setUserBanStatus } from "../../store/profile/profileActions";
import { useDispatch } from "react-redux";
import { TutorTab } from "./tutorTab";
import { StudentTab } from "./studentTab";
import { ProfileRole } from "../../store/profile/profileSlice";
import { UserStatus } from "../../store/auth/authSlice";
import { errorToast, getDeviceTimeZone, getImage, mongoIdToString, successToast } from "../../utils";
import axiosConfig from "../../utils/axiosConfig";
import moment from "moment";

const personaStorageKey = (userId) => `adminUserPersona:${userId}`;

const UserProfile = () => {
    const params = useParams();
    const [activeTab, setActiveTab] = useState(0);
    const [activePersona, setActivePersona] = useState(ProfileRole.TUTOR);
    const [userProfiles, setUserProfiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [personaLoading, setPersonaLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [tutorProfile, setTutorProfile] = useState(null);
    const [studentProfile, setStudentProfile] = useState(null);
    const [tutorFullProfile, setTutorFullProfile] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [walletStats, setWalletStats] = useState(null);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [profileChats, setProfileChats] = useState([]);
    const [tutorWithdrawCount, setTutorWithdrawCount] = useState(null);
    const [isBanLoading, setIsBanLoading] = useState(false);
    const [showBanDialog, setShowBanDialog] = useState(false);
    const dispatch = useDispatch();
    const isUserBanned = user?.status === UserStatus.BANNED;

    const tutorIdStr = mongoIdToString(tutorProfile?._id);
    const studentIdStr = mongoIdToString(studentProfile?._id);
    const hasTutor = Boolean(tutorIdStr);
    const hasStudent = Boolean(studentIdStr);
    const hasDualProfile = hasTutor && hasStudent;

    const activeProfile = useMemo(() => {
        if (!hasTutor && !hasStudent) return null;
        if (hasTutor && hasStudent) {
            return activePersona === ProfileRole.STUDENT
                ? { id: studentIdStr, apiRole: ProfileRole.STUDENT }
                : { id: tutorIdStr, apiRole: ProfileRole.TUTOR };
        }
        if (hasTutor) {
            return { id: tutorIdStr, apiRole: ProfileRole.TUTOR };
        }
        return { id: studentIdStr, apiRole: ProfileRole.STUDENT };
    }, [hasTutor, hasStudent, activePersona, tutorIdStr, studentIdStr]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setLoadError(null);
        (async () => {
            try {
                const userWithProfiles = await dispatch(getUserWithProfiles({ userId: params.userId })).unwrap();
                if (cancelled) return;
                setUser(userWithProfiles?.user || null);
                let list = userWithProfiles?.profile;
                if (!Array.isArray(list)) {
                    list = await dispatch(getUserProfiles({ userId: params.userId })).unwrap();
                }
                list = Array.isArray(list) ? list : [];
                setUserProfiles(list);

                const tutor = list.find((v) => v.role === ProfileRole.TUTOR || v.role === 2 || v.role === "2" || v.role === "tutor") || null;
                const student = list.find((v) => v.role === ProfileRole.STUDENT || v.role === 3 || v.role === "3" || v.role === "student") || null;
                setTutorProfile(tutor);
                setStudentProfile(student);

                if (tutor && student) {
                    const saved = localStorage.getItem(personaStorageKey(params.userId));
                    setActivePersona(saved === ProfileRole.STUDENT ? ProfileRole.STUDENT : ProfileRole.TUTOR);
                } else if (student) {
                    setActivePersona(ProfileRole.STUDENT);
                } else {
                    setActivePersona(ProfileRole.TUTOR);
                }

                const tutorIdForFull = mongoIdToString(tutor?._id);
                if (tutor) {
                    try {
                        const body = tutorIdForFull
                            ? { id: tutorIdForFull, userId: params.userId }
                            : { userId: params.userId };
                        const res = await axiosConfig.post("/admin/profile/full", body);
                        if (!cancelled) setTutorFullProfile(res.data || null);
                    } catch {
                        if (!cancelled) setTutorFullProfile(null);
                    }
                } else if (!cancelled) {
                    setTutorFullProfile(null);
                }
            } catch (error) {
                if (!cancelled) {
                    const msg = error?.error || error?.message || "Could not load user profile";
                    setLoadError(msg);
                    errorToast(msg);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [dispatch, params.userId]);

    useEffect(() => {
        if (!activeProfile?.id) {
            setAppointments([]);
            setWalletStats(null);
            setDashboardStats(null);
            setProfileChats([]);
            setTutorWithdrawCount(null);
            return;
        }

        let cancelled = false;
        const tz = getDeviceTimeZone();
        setPersonaLoading(true);

        (async () => {
            try {
                const [apptR, walletR, chatR, dashR] = await Promise.allSettled([
                    axiosConfig.post("/appointment/get/appointment", {
                        profile: activeProfile.id,
                        role: activeProfile.apiRole,
                        timezone: tz
                    }),
                    axiosConfig.post("/wallet/stats", {
                        profile: activeProfile.id,
                        role: activeProfile.apiRole
                    }),
                    axiosConfig.post("/chat/admin/list", { profileId: activeProfile.id }),
                    axiosConfig.post("/admin/profile/dashboard/stat", {
                        profile: activeProfile.id,
                        role: activeProfile.apiRole
                    })
                ]);

                if (cancelled) return;

                setAppointments(
                    apptR.status === "fulfilled" && Array.isArray(apptR.value.data) ? apptR.value.data : []
                );
                setWalletStats(walletR.status === "fulfilled" ? walletR.value.data ?? null : null);

                if (chatR.status === "fulfilled" && Array.isArray(chatR.value.data)) {
                    setProfileChats(chatR.value.data);
                } else {
                    setProfileChats([]);
                }

                setDashboardStats(dashR.status === "fulfilled" ? dashR.value.data ?? null : null);

                if (activeProfile.apiRole === ProfileRole.TUTOR) {
                    try {
                        const w = await axiosConfig.post("/wallet/withdraw/list", { tutor: activeProfile.id });
                        if (!cancelled) {
                            setTutorWithdrawCount(Array.isArray(w.data) ? w.data.length : 0);
                        }
                    } catch {
                        if (!cancelled) setTutorWithdrawCount(null);
                    }
                } else if (!cancelled) {
                    setTutorWithdrawCount(null);
                }
            } finally {
                if (!cancelled) setPersonaLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [activeProfile]);

    const setPersona = useCallback(
        (persona) => {
            setActivePersona(persona);
            if (hasDualProfile) {
                localStorage.setItem(personaStorageKey(params.userId), persona);
            }
        },
        [hasDualProfile, params.userId]
    );

    /** Chart + summary counts must follow the persona toggle; also drops rows if the API returns a broader set. */
    const appointmentsForActivePersona = useMemo(() => {
        const pid = activeProfile?.id;
        const role = activeProfile?.apiRole;
        if (!pid || !role) return [];
        return appointments.filter((item) => {
            const asTutor = mongoIdToString(item.tutor);
            const asStudent = mongoIdToString(item.student);
            if (role === ProfileRole.TUTOR) return asTutor === pid;
            if (role === ProfileRole.STUDENT) return asStudent === pid;
            return false;
        });
    }, [appointments, activeProfile?.id, activeProfile?.apiRole]);

    const appointmentStats = useMemo(() => {
        const statusMap = {
            completed: 0,
            pending: 0,
            canceled: 0
        };

        const monthCounts = {};
        let totalEarnings = 0;

        appointmentsForActivePersona.forEach((item) => {
            if (item.status === 1) {
                statusMap.completed += 1;
                totalEarnings += Number(item.price || 0);
            } else if (item.status === 2) {
                statusMap.canceled += 1;
            } else {
                statusMap.pending += 1;
            }

            if (item?.date) {
                const monthKey = moment(item.date).format("MMM");
                monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1;
            }
        });

        const chartData = [["Month", "Appointments"]];
        const last6Months = [];
        for (let i = 5; i >= 0; i -= 1) {
            const key = moment().subtract(i, "months").format("MMM");
            last6Months.push(key);
        }
        last6Months.forEach((month) => chartData.push([month, monthCounts[month] || 0]));

        const attendedTotal = statusMap.completed + statusMap.canceled;
        const attendance = attendedTotal > 0 ? Math.round((statusMap.completed / attendedTotal) * 100) : 0;

        return {
            ...statusMap,
            attendance,
            totalAppointments: appointmentsForActivePersona.length,
            totalEarnings,
            chartData,
            callMinutes: statusMap.completed * 50
        };
    }, [appointmentsForActivePersona]);

    const topStats = useMemo(() => {
        const d = dashboardStats || {};
        const walletBalance = Number(walletStats?.balance ?? walletStats?.available ?? 0);
        return {
            attendancePct:
                d.attendance != null && !Number.isNaN(Number(d.attendance))
                    ? Math.round(Number(d.attendance))
                    : appointmentStats.attendance,
            lessons: d.lessons != null ? Number(d.lessons) : appointmentStats.completed,
            appointmentsCount: appointmentStats.totalAppointments,
            walletBalance,
            profit: d.profit != null ? Number(d.profit) : appointmentStats.totalEarnings
        };
    }, [dashboardStats, appointmentStats, walletStats]);

    const tutorDocumentStats = useMemo(() => {
        const education = tutorFullProfile?.education?.length || tutorProfile?.education?.length || 0;
        const experience = tutorFullProfile?.experience?.length || tutorProfile?.experience?.length || 0;
        const certificate = tutorFullProfile?.certificate?.length || tutorProfile?.certificate?.length || 0;
        return {
            education,
            experience,
            certificate,
            totalCourses: education + experience
        };
    }, [tutorFullProfile, tutorProfile]);

    const onConfirmToggleBanStatus = () => {
        if (isBanLoading) return;
        const targetUserId = user?._id || user?.id || params?.userId;
        if (!targetUserId) {
            errorToast("User id not found");
            return;
        }
        const isBanned = user.status === UserStatus.BANNED;

        setIsBanLoading(true);
        dispatch(setUserBanStatus({ userId: targetUserId, ban: !isBanned }))
            .unwrap()
            .then((updatedUser) => {
                setUser((prev) => ({ ...prev, ...updatedUser }));
                successToast(`User ${isBanned ? "unbanned" : "banned"} successfully`);
                setShowBanDialog(false);
            })
            .catch((error) => {
                errorToast(error?.error || "Could not update user status");
            })
            .finally(() => {
                setIsBanLoading(false);
            });
    };

    const onToggleBanStatus = () => {
        if (isBanLoading) return;
        setShowBanDialog(true);
    };

    const profileRoleLabel = (role) => {
        if (role === ProfileRole.TUTOR || role === 2 || role === "2" || role === "tutor") return "Tutor";
        if (role === ProfileRole.STUDENT || role === 3 || role === "3" || role === "student") return "Student";
        if (role === ProfileRole.ADMIN || role === 1 || role === "1" || role === "admin") return "Admin";
        return String(role || "Unknown");
    };

    const isTutorView = activeProfile?.apiRole === ProfileRole.TUTOR;
    const profitTitle = isTutorView ? "Profit" : "Lesson spend";

    const selectedProfileDoc = useMemo(() => {
        if (!activeProfile?.id || !userProfiles.length) return null;
        return userProfiles.find((p) => mongoIdToString(p._id) === activeProfile.id) ?? null;
    }, [userProfiles, activeProfile?.id]);

    const chatRowId = (chat) => mongoIdToString(chat?._id) || String(chat?._id ?? "");

    return (
        <>
            {isLoading && (
                <div className="rounded-2xl bg-white p-6 text-center text-sm text-nt-grey">Loading user…</div>
            )}

            {!isLoading && !user && (
                <div className="rounded-2xl bg-white p-6">
                    <p className="font-medium text-nt-blue">User could not be loaded</p>
                    {loadError && <p className="mt-2 text-sm text-red-600">{loadError}</p>}
                    <p className="mt-2 text-sm text-nt-grey">
                        Requested id: <span className="font-mono">{params.userId}</span>
                    </p>
                    <Link className="mt-4 inline-block text-nt-blue underline" to="/users/list">
                        Back to users
                    </Link>
                </div>
            )}

            {!isLoading && user && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-4">
                    <ul className={`${Styles.breadcumb} flex flex-wrap items-center gap-2`}>
                        <li className="flex">
                            <Link className="flex items-center gap-2 text-nt-grey no-underline" to={"/users/list"}>
                                <FiArrowLeft />
                                Users
                            </Link>
                        </li>
                        <li>
                            {user.firstName} {user.lastName}
                        </li>
                    </ul>
                    <div className="flex flex-wrap items-center gap-3">
                        {hasDualProfile && (
                            <div
                                className="flex rounded-lg border border-gray-200 p-0.5 text-sm"
                                role="group"
                                aria-label="View metrics as"
                            >
                                <button
                                    type="button"
                                    onClick={() => setPersona(ProfileRole.TUTOR)}
                                    className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                                        activePersona === ProfileRole.TUTOR
                                            ? "bg-nt-blue text-white"
                                            : "text-nt-grey hover:bg-gray-50"
                                    }`}
                                >
                                    As tutor
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPersona(ProfileRole.STUDENT)}
                                    className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                                        activePersona === ProfileRole.STUDENT
                                            ? "bg-nt-blue text-white"
                                            : "text-nt-grey hover:bg-gray-50"
                                    }`}
                                >
                                    As student
                                </button>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={onToggleBanStatus}
                            disabled={isBanLoading}
                            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                                isUserBanned
                                    ? "border-green-600 bg-green-50 text-green-700 hover:bg-green-100"
                                    : "border-red-600 bg-red-50 text-red-700 hover:bg-red-100"
                            } ${isBanLoading ? "cursor-not-allowed opacity-70" : ""}`}
                        >
                            {isBanLoading ? "Saving..." : isUserBanned ? "Unban User" : "Ban User"}
                        </button>
                    </div>
                </div>
            )}

            {!isLoading && user && (
                <>
                    {activeProfile && (
                        <p className="mt-2 text-sm text-nt-grey">
                            Metrics and activity below reflect this user as a{" "}
                            <span className="font-medium text-nt-blue">
                                {activeProfile.apiRole === ProfileRole.TUTOR ? "tutor" : "student"}
                            </span>
                            {personaLoading ? " (updating…)" : ""}.
                        </p>
                    )}

                    <div className="mt-5">
                        <div className="general-informations mb-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                    <NumberIcon title="Attendance" number={`${topStats.attendancePct}%`} Icon={FiUsers} />
                    <NumberIcon title="Lessons" number={topStats.lessons} Icon={FiBookOpen} />
                    <NumberIcon title="Appointments" number={topStats.appointmentsCount} Icon={FiCalendar} />
                    <NumberIcon title="Wallet" number={`${topStats.walletBalance.toFixed(2)} €`} Icon={FiCreditCard} />
                            <NumberIcon title={profitTitle} number={`${topStats.profit.toFixed(2)} €`} Icon={FaMoneyBillTrendUp} />
                        </div>
                    </div>

                    <div className="mt-5">
                        {activeTab === 0 && (
                    <div className="space-y-5">
                        <div className="grid grid-cols-1 gap-4 rounded-2xl bg-white p-4 lg:grid-cols-3">
                            <div className="flex items-center gap-3">
                                <img
                                    src={getImage(user?.avatar?.path || user?.avatar, "AVATAR")}
                                    alt="user"
                                    className="h-16 w-16 rounded-full object-cover"
                                />
                                <div>
                                    <h3 className="text-lg font-semibold text-nt-blue">
                                        {user?.firstName} {user?.lastName}
                                    </h3>
                                    <p className="text-sm text-nt-grey">{user?.email || "No email"}</p>
                                    <p className="text-sm text-nt-grey">{user?.phone || "No phone number"}</p>
                                </div>
                            </div>
                            <div className="space-y-2 rounded-xl bg-gray-50 p-3 text-sm">
                                <p className="flex items-center gap-2">
                                    <FiUser /> Status:{" "}
                                    <span className="font-medium">{isUserBanned ? "Banned" : "Active"}</span>
                                </p>
                                <p className="flex items-center gap-2">
                                    <FiUsers /> Profiles on account:{" "}
                                    <span className="font-medium">{userProfiles.length}</span>
                                </p>
                                <p className="flex items-center gap-2">
                                    <FiPhone /> Call Minutes:{" "}
                                    <span className="font-medium">{appointmentStats.callMinutes}</span>
                                </p>
                                {isTutorView && tutorWithdrawCount != null && (
                                    <p className="flex items-center gap-2 text-nt-grey">
                                        Withdraw requests: <span className="font-medium">{tutorWithdrawCount}</span>
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2 rounded-xl bg-gray-50 p-3 text-sm">
                                <p className="flex items-center gap-2">
                                    <FiDollarSign /> {profitTitle}:{" "}
                                    <span className="font-medium">{topStats.profit.toFixed(2)} €</span>
                                </p>
                                {isTutorView ? (
                                    <p className="flex items-center gap-2">
                                        <FiBookOpen /> Courses/Documents:{" "}
                                        <span className="font-medium">{tutorDocumentStats.totalCourses}</span>
                                    </p>
                                ) : (
                                    <p className="flex items-center gap-2">
                                        <FiBookOpen /> Free lessons:{" "}
                                        <span className="font-medium">
                                            {selectedProfileDoc?.freeLesson ?? 0}
                                        </span>
                                    </p>
                                )}
                                <p className="flex items-center gap-2">
                                    <FiCalendar /> Pending Appointments:{" "}
                                    <span className="font-medium">{appointmentStats.pending}</span>
                                </p>
                                <p className="flex items-center gap-2">
                                    <FiAlertTriangle /> Canceled:{" "}
                                    <span className="font-medium">{appointmentStats.canceled}</span>
                                </p>
                                {dashboardStats?.students != null && isTutorView && (
                                    <p className="flex items-center gap-2">
                                        <FiUsers /> Distinct students:{" "}
                                        <span className="font-medium">{dashboardStats.students}</span>
                                    </p>
                                )}
                            </div>
                        </div>

                        {isTutorView &&
                            tutorFullProfile?.globalReviews &&
                            (tutorFullProfile.globalReviews.count > 0 || tutorFullProfile.globalReviews.stars > 0) && (
                            <div className="rounded-2xl bg-white p-4">
                                <h3 className="mb-2 flex items-center gap-2 text-lg font-semibold text-nt-blue">
                                    <FiStar className="text-amber-500" />
                                    Tutor reviews
                                </h3>
                                <p className="text-sm text-nt-grey">
                                    Average {tutorFullProfile.globalReviews.stars ?? 0} / 5 from{" "}
                                    {tutorFullProfile.globalReviews.count ?? 0} review(s).
                                </p>
                                {Array.isArray(tutorFullProfile.reviews) && tutorFullProfile.reviews.length > 0 && (
                                    <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto text-sm">
                                        {tutorFullProfile.reviews.slice(0, 5).map((rev) => (
                                            <li key={rev._id} className="rounded border border-gray-100 p-2">
                                                <span className="font-medium text-nt-blue">
                                                    {rev.student?.userId?.firstName} {rev.student?.userId?.lastName}
                                                </span>
                                                <span className="text-nt-grey">
                                                    {" "}
                                                    — WiFi {rev.wifi}, Quality {rev.quality}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {appointmentStats.totalAppointments > 0 && (
                            <div className="rounded-2xl bg-white p-4">
                                <h3 className="mb-3 text-lg font-semibold text-nt-blue">Appointments Trend (Last 6 Months)</h3>
                                <BarChart
                                    data={appointmentStats.chartData}
                                    options={{
                                        legend: { position: "none" },
                                        colors: ["#191D51"],
                                        chartArea: { width: "85%", height: "70%" },
                                        hAxis: { title: "Month" },
                                        vAxis: { title: "Appointments", minValue: 0 }
                                    }}
                                />
                            </div>
                        )}

                        {selectedProfileDoc && (
                            <div className="rounded-2xl bg-white p-4">
                                <h3 className="mb-3 text-lg font-semibold text-nt-blue">
                                    {profileRoleLabel(selectedProfileDoc.role)} profile
                                </h3>
                                <p className="text-sm text-nt-grey">
                                    Profile ID:{" "}
                                    <span className="font-mono text-nt-blue">{mongoIdToString(selectedProfileDoc._id)}</span>
                                </p>
                                {isTutorView && (
                                    <div className="mt-3 space-y-1 text-sm text-nt-grey">
                                        <p>Education: {tutorDocumentStats.education}</p>
                                        <p>Experience: {tutorDocumentStats.experience}</p>
                                        <p>Certificates: {tutorDocumentStats.certificate}</p>
                                    </div>
                                )}
                                {!isTutorView && (
                                    <div className="mt-3 space-y-1 text-sm text-nt-grey">
                                        <p>Free lessons: {selectedProfileDoc.freeLesson ?? 0}</p>
                                        <p>Onboarding complete: {selectedProfileDoc.lastStep ? "Yes" : "No"}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeProfile && (
                            <div className="rounded-2xl bg-white p-4">
                                <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-nt-blue">
                                    <FiMessageCircle />
                                    Chats ({profileChats.length})
                                </h3>
                                {personaLoading ? (
                                    <p className="text-sm text-nt-grey">Loading conversations…</p>
                                ) : profileChats.length === 0 ? (
                                    <p className="text-sm text-nt-grey">No conversations for this profile.</p>
                                ) : (
                                    <ul className="max-h-72 space-y-2 overflow-y-auto">
                                        {profileChats.map((chat, idx) => {
                                            const cid = chatRowId(chat);
                                            const u = chat.user || {};
                                            const preview = chat.message?.isFile
                                                ? "File message"
                                                : chat.message?.message || "—";
                                            return (
                                                <li
                                                    key={cid || `chat-${idx}`}
                                                    className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 text-sm"
                                                >
                                                    <img
                                                        src={getImage(u.avatar, "AVATAR")}
                                                        alt=""
                                                        className="h-10 w-10 shrink-0 rounded-full object-cover"
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate font-medium text-nt-blue capitalize">
                                                            {u.firstName} {u.lastName}
                                                        </p>
                                                        <p className="truncate text-nt-grey">{preview}</p>
                                                    </div>
                                                    <Link
                                                        to={`/chat/${cid}`}
                                                        className="shrink-0 rounded-md border border-nt-blue px-2 py-1 text-xs font-medium text-nt-blue no-underline hover:bg-nt-blue hover:text-white"
                                                    >
                                                        Open
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}

                        {activeProfile && (
                            <div className="rounded-2xl bg-white p-4">
                                <h3 className="mb-3 text-lg font-semibold text-nt-blue">Recent appointments (selected profile)</h3>
                                <AppointmentWidget
                                    embedded
                                    profile={activeProfile.id}
                                    role={activeProfile.apiRole}
                                    appointments={appointmentsForActivePersona}
                                    searchPlaceholder="Search by name or date..."
                                />
                            </div>
                        )}
                    </div>
                )}
                        {activeTab === 1 && !isLoading && tutorProfile && <TutorTab profile={tutorProfile} />}
                        {activeTab === 2 && !isLoading && studentProfile && <StudentTab profile={studentProfile} />}
                    </div>
                </>
            )}

            {showBanDialog && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => !isBanLoading && setShowBanDialog(false)}
                >
                    <div
                        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-3 flex items-start gap-3">
                            <div
                                className={`mt-0.5 rounded-full p-2 ${
                                    isUserBanned ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                                }`}
                            >
                                <FiAlertTriangle />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-nt-blue">
                                    {isUserBanned ? "Unban user" : "Ban user"}
                                </h3>
                                <p className="text-sm text-nt-grey">
                                    {user?.firstName} {user?.lastName}
                                    {user?.email ? ` (${user.email})` : ""}
                                </p>
                            </div>
                        </div>
                        <p className="mb-5 text-sm leading-6 text-nt-grey">
                            {isUserBanned
                                ? "This user will regain access to the platform and be able to login again."
                                : "This action will immediately block this user from logging in and using the platform."}
                            <span className="block font-medium text-nt-blue">Are you sure you want to continue?</span>
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                onClick={() => setShowBanDialog(false)}
                                disabled={isBanLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={onConfirmToggleBanStatus}
                                disabled={isBanLoading}
                                className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                                    isUserBanned
                                        ? "border-green-600 bg-green-50 text-green-700 hover:bg-green-100"
                                        : "border-red-600 bg-red-50 text-red-700 hover:bg-red-100"
                                } ${isBanLoading ? "cursor-not-allowed opacity-70" : ""}`}
                            >
                                {isBanLoading ? "Saving..." : isUserBanned ? "Yes, unban" : "Yes, ban"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export { UserProfile };
